/**
 * @engine/core - Transform
 * 
 * Represents position, rotation and scale of a GameObject.
 * Supports hierarchy (parent-child relationships).
 */

import { Vec2, Vec3 } from '@engine/math';
import type { GameObjectId, TransformData } from './types';

const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;

/**
 * Transform component - handles position, rotation, scale and hierarchy
 */
export class Transform {
  // Local transform (relative to parent)
  private _localPosition: Vec3 = new Vec3(0, 0, 0);
  private _localRotation: number = 0; // degrees
  private _localScale: Vec2 = new Vec2(1, 1);
  
  // Cached world transform
  private _worldPosition: Vec3 = new Vec3(0, 0, 0);
  private _worldRotation: number = 0;
  private _worldScale: Vec2 = new Vec2(1, 1);
  
  // Dirty flags
  private _dirty: boolean = true;
  private _worldDirty: boolean = true;
  
  // Hierarchy
  private _parent: Transform | null = null;
  private _children: Transform[] = [];
  
  // Owner
  private _gameObjectId: GameObjectId;
  
  constructor(gameObjectId: GameObjectId, data?: Partial<TransformData>) {
    this._gameObjectId = gameObjectId;
    
    if (data) {
      if (data.localPosition) {
        this._localPosition.set(
          data.localPosition.x,
          data.localPosition.y,
          data.localPosition.z
        );
      }
      if (data.localRotation !== undefined) {
        this._localRotation = data.localRotation;
      }
      if (data.localScale) {
        this._localScale.set(data.localScale.x, data.localScale.y);
      }
    }
  }
  
  // ==========================================================================
  // LOCAL PROPERTIES
  // ==========================================================================
  
  /** Local position relative to parent */
  get localPosition(): Vec3 {
    return this._localPosition;
  }
  
  set localPosition(value: Vec3) {
    this._localPosition.copy(value);
    this.setDirty();
  }
  
  /** Local rotation in degrees */
  get localRotation(): number {
    return this._localRotation;
  }
  
  set localRotation(value: number) {
    this._localRotation = value;
    this.setDirty();
  }

  /** Local rotation in radians */
  get localRotationRadians(): number {
    return this._localRotation * DEG2RAD;
  }

  set localRotationRadians(value: number) {
    this._localRotation = value * RAD2DEG;
    this.setDirty();
  }
  
  /** Local scale */
  get localScale(): Vec2 {
    return this._localScale;
  }
  
  set localScale(value: Vec2) {
    this._localScale.copy(value);
    this.setDirty();
  }
  
  // ==========================================================================
  // WORLD PROPERTIES
  // ==========================================================================
  
  /** World position */
  get position(): Vec3 {
    this.updateWorldTransform();
    return this._worldPosition;
  }
  
  set position(value: Vec3) {
    if (this._parent) {
      // Convert world to local
      // For now, simplified - just subtract parent position
      this._parent.updateWorldTransform();
      this._localPosition.set(
        value.x - this._parent._worldPosition.x,
        value.y - this._parent._worldPosition.y,
        value.z - this._parent._worldPosition.z
      );
    } else {
      this._localPosition.copy(value);
    }
    this.setDirty();
  }
  
  /** World rotation in degrees */
  get rotation(): number {
    this.updateWorldTransform();
    return this._worldRotation;
  }
  
  set rotation(value: number) {
    if (this._parent) {
      this._parent.updateWorldTransform();
      this._localRotation = value - this._parent._worldRotation;
    } else {
      this._localRotation = value;
    }
    this.setDirty();
  }

  /** World rotation in radians */
  get rotationRadians(): number {
    this.updateWorldTransform();
    return this._worldRotation * DEG2RAD;
  }

  set rotationRadians(value: number) {
    if (this._parent) {
      this._parent.updateWorldTransform();
      this._localRotation = value * RAD2DEG - this._parent._worldRotation;
    } else {
      this._localRotation = value * RAD2DEG;
    }
    this.setDirty();
  }
  
  /** World scale (lossy - may not be accurate with rotated parents) */
  get lossyScale(): Vec2 {
    this.updateWorldTransform();
    return this._worldScale;
  }
  
  // ==========================================================================
  // HIERARCHY
  // ==========================================================================
  
  /** Parent transform */
  get parent(): Transform | null {
    return this._parent;
  }
  
  set parent(value: Transform | null) {
    if (this._parent === value) return;
    
    // Remove from old parent
    if (this._parent) {
      const index = this._parent._children.indexOf(this);
      if (index !== -1) {
        this._parent._children.splice(index, 1);
      }
    }
    
    // Add to new parent
    this._parent = value;
    if (value) {
      value._children.push(this);
    }
    
    this.setWorldDirty();
  }
  
  /** Child count */
  get childCount(): number {
    return this._children.length;
  }
  
  /** Get child by index */
  getChild(index: number): Transform | null {
    return this._children[index] ?? null;
  }
  
  /** Get all children */
  get children(): readonly Transform[] {
    return this._children;
  }
  
  /** Owner GameObject ID */
  get gameObjectId(): GameObjectId {
    return this._gameObjectId;
  }
  
  // ==========================================================================
  // TRANSFORM METHODS
  // ==========================================================================
  
  /**
   * Translate by offset
   */
  translate(x: number, y: number, z: number = 0): void {
    this._localPosition.x += x;
    this._localPosition.y += y;
    this._localPosition.z += z;
    this.setDirty();
  }
  
  /**
   * Rotate by angle in degrees
   */
  rotate(angle: number): void {
    this._localRotation += angle;
    this.setDirty();
  }
  
  /**
   * Set local position from components
   */
  setLocalPosition(x: number, y: number, z: number = 0): void {
    this._localPosition.set(x, y, z);
    this.setDirty();
  }
  
  /**
   * Set local scale from components
   */
  setLocalScale(x: number, y: number): void {
    this._localScale.set(x, y);
    this.setDirty();
  }
  
  // ==========================================================================
  // SERIALIZATION
  // ==========================================================================
  
  /**
   * Serialize to data
   */
  toData(): TransformData {
    return {
      localPosition: {
        x: this._localPosition.x,
        y: this._localPosition.y,
        z: this._localPosition.z,
      },
      localRotation: this._localRotation,
      localScale: {
        x: this._localScale.x,
        y: this._localScale.y,
      },
    };
  }
  
  /**
   * Load from data
   */
  fromData(data: TransformData): void {
    this._localPosition.set(
      data.localPosition.x,
      data.localPosition.y,
      data.localPosition.z
    );
    this._localRotation = data.localRotation;
    this._localScale.set(data.localScale.x, data.localScale.y);
    this.setDirty();
  }
  
  // ==========================================================================
  // PUBLIC DIRTY FLAG
  // ==========================================================================
  
  /** Mark transform as dirty (call after modifying localPosition/localScale components directly) */
  markDirty(): void {
    this.setDirty();
  }
  
  // ==========================================================================
  // INTERNAL
  // ==========================================================================
  
  private setDirty(): void {
    this._dirty = true;
    this.setWorldDirty();
  }
  
  private setWorldDirty(): void {
    this._worldDirty = true;
    // Propagate to children
    for (const child of this._children) {
      child.setWorldDirty();
    }
  }
  
  private updateWorldTransform(): void {
    if (!this._worldDirty) return;
    
    this._dirty = false;
    
    if (this._parent) {
      this._parent.updateWorldTransform();
      
      // Proper matrix multiplication for rotated/scaled parents
      const parentRot = this._parent._worldRotation * DEG2RAD;
      const cos = Math.cos(parentRot);
      const sin = Math.sin(parentRot);
      
      // Transform local position by parent's rotation and scale
      const scaledX = this._localPosition.x * this._parent._worldScale.x;
      const scaledY = this._localPosition.y * this._parent._worldScale.y;
      
      this._worldPosition.set(
        this._parent._worldPosition.x + scaledX * cos - scaledY * sin,
        this._parent._worldPosition.y + scaledX * sin + scaledY * cos,
        this._parent._worldPosition.z + this._localPosition.z
      );
      this._worldRotation = this._parent._worldRotation + this._localRotation;
      this._worldScale.set(
        this._parent._worldScale.x * this._localScale.x,
        this._parent._worldScale.y * this._localScale.y
      );
    } else {
      this._worldPosition.copy(this._localPosition);
      this._worldRotation = this._localRotation;
      this._worldScale.copy(this._localScale);
    }
    
    this._worldDirty = false;
  }
}
